import path from 'path';
import querystring from 'querystring';
import 'navigator.sendbeacon';

import Logger from '@cvent/nucleus-logging';
const LOG = new Logger('event-guestside-site/src/main');

import loadCookieConsent from './appInitialization/loadCookieConsent';
import history from './myHistory';
import { createRouteHandlers } from './appInitialization/routeHandlers';
import { loadCustomFonts } from 'nucleus-widgets/redux/modules/customFonts';
import { registerTranslation, setCurrencyCode, setLocale } from 'nucleus-guestside-site/src/redux/modules/text';
import { withRoutes } from './appInitialization/routeHandlerComponent';
import { createApp } from './App';
import { loadLandingPageContent, loadLanguageManagementSettings } from './redux/actions';
import { loadEventTimezoneIntoStore } from './redux/timezones';
import { initializeNetworkErrorDialog } from './errorHandling/NetworkErrorDialog';
import { loadLanguage } from './redux/multiLanguage/actions';
import detectBrowserFeatures from 'nucleus-widgets/utils/browserFeatureDetection';

import React from 'react';
import { render } from 'react-dom';

import { Provider } from 'react-redux';
import Title from './appInitialization/Title';
import {
  Route,
  Redirect,
  Switch,
  Router
} from 'react-router-dom';
import DialogContainer from 'nucleus-guestside-site/src/containers/DialogContainer';

// Get the event widget factory.
import WidgetFactory from './widgetFactory';
import TransparentWrapper from './dialogs/TransparentWrapper';

import { initializeFetchWithSessionTimeout } from './dialogs/SessionTimedOutDialog';
import configureStore from './redux/configureStore';
import SubstitutionCartClient from './clients/SubstitutionCartClient';
import TravelApiClient from './clients/TravelApiClient';
import EventEmailClient from './clients/EventEmailClient';
import PasskeyClient from './clients/PasskeyClient';
import FlexFileClient from './clients/FlexFileClient';
import DataTagsResolutionClient from './clients/DataTagsResolutionClient';
import ExternalAuthClient from './clients/ExternalAuthClient';
import InviteeSearchClient from './clients/InviteeSearchClient';
import WebsitePasswordClient from './clients/WebsitePasswordClient';
import CreditCardClient from './clients/CreditCardClient';
import EventPersonaClient from './clients/EventPersonaClient';
import AttendeeListClient from 'event-widgets/clients/AttendeeListClient';
import AppointmentsClient from 'event-widgets/clients/AppointmentsClient';
import CcpaComplianceClient from './clients/CcpaComplianceClient';
import AttendeeLoginClient from './clients/AttendeeLoginClient';
import LimitsClient from './clients/LimitsClient';
import locales from './appInitialization/locales';
import { loadIntlWithLocale } from 'nucleus-text/intlLoaders/index';
import getMatchingLocale from 'nucleus-text/util/getMatchingLocale';
import { createLoadAccountSnapshotAction, createLoadEventSnapshotAction } from './redux/actions';
import { loadTravelSnapshotDataIntoStore } from 'event-widgets/redux/modules/eventTravel';
import { loginRegistrant } from './redux/registrantLogin/actions';
import { setSelectedTimeZone } from './redux/timeZoneSelection';
import { setCurrencies } from './redux/currencies';
import { loadCapacityCounts } from 'event-widgets/redux/modules/capacity';
import { setAirports } from './redux/airports';
import EventFeeClient from 'event-widgets/clients/EventFeeClient';
import UserTextClient from 'event-widgets/clients/UserTextClient';
import AttendeeOrderClient from './clients/AttendeeOrderClient';
import { styleString } from '@cvent/carina-rich-text-renderer';
import TranslationSnapshotClient from 'event-widgets/clients/TranslationSnapshotClient';
import { createLoadCountry } from 'event-widgets/redux/modules/country';
import { getUserType } from './redux/userSession';
import { getFullLanguageFromDefaultMapping } from 'event-widgets/utils/getMatchingLocale';
import { eventSingleLanguageDefaultNotAccountDefault, registerTranslationsFromSnapshot }
  from 'event-widgets/utils/multiLanguageUtils';
import UniversalWebcastClient from '../../../pkgs/event-widgets/clients/UniversalWebcastClient';
import { convertEventTimezoneTranslations } from 'event-widgets/redux/selectors/timezone';
import { eventHasMultipleLanguages,
  findMatchingLocaleFromAvailableLocales } from 'event-widgets/utils/multiLanguageUtils';

/*
 * testSettings allows settings to be changed in services by passing in query params.
 * For example, we can set the dequeueTimeout by passing a parameter
 */
function getTestSettings(queryParams) {
  return {
    registrationCheckoutTimeout: queryParams.registrationCheckoutTimeout
  };
}

// Register Translations
async function registerTranslations(textResolverFn, cultureCode) {
  const translationsPromise = new Promise(getMatchingLocale(cultureCode, locales));
  await new Promise(resolve => loadIntlWithLocale(cultureCode, resolve));
  textResolverFn.registerTranslations(await translationsPromise, 'default', cultureCode);
}

// eslint-disable-next-line complexity
async function startAppRender(appSettings, preloadedData) {
  render(
    <div />,
    document.getElementById('react-mount')
  );

  const viewRoot = window && window.location && window.location.pathname
    && ('/' + window.location.pathname.split('/')[1]) || appSettings.viewRoot;
  const queryParams = querystring.parse(window.location.search.slice(1));
  const testSettings = appSettings.eventContext.allowDevMode ? getTestSettings(queryParams) : {};
  const eventId = appSettings.eventContext.eventId;
  const plannerRegViewRoot = appSettings.viewRoot;
  const normalRegViewRoot = path.join(viewRoot, eventId);
  const rootPath = appSettings.eventContext.isPlanner ? plannerRegViewRoot : normalRegViewRoot;
  const baseUrl = `${appSettings.serviceBaseUrl}/v1/`;
  const calendarBasePath = `${baseUrl}event/ical/${eventId}`;
  const analyticsUrl = appSettings.analyticsUrl;
  const analyticsUrlPath = appSettings.analyticsUrlPath;
  let eventLaunchWizardSettings = appSettings.eventLaunchWizardSettings;
  const httpReferrer = document.referrer;

  if (!eventLaunchWizardSettings) {
    eventLaunchWizardSettings = {};
  } else {
    eventLaunchWizardSettings = JSON.parse(eventLaunchWizardSettings);
  }
  const { isMerchantCPS = false, showBankAccount = false, merchantAccountId = null,
    openLaunchWizard = false, remittanceType = '' } = eventLaunchWizardSettings;

  const userType = getUserType(appSettings.eventContext);

  const store = configureStore({
    account: { name: appSettings.accountName },
    accessToken: appSettings.accessToken,
    environment: appSettings.environment,
    testSettings,
    clients: {
      eventEmailClient: new EventEmailClient(baseUrl, eventId, appSettings.environment),
      lookupClient: preloadedData.clients.lookupClient,
      capacityClient: preloadedData.clients.capacityClient,
      regCartClient: preloadedData.clients.regCartClient,
      eventSnapshotClient: preloadedData.clients.eventSnapshotClient,
      substitutionCartClient: new SubstitutionCartClient(baseUrl, eventId, appSettings.environment, userType),
      eventGuestClient: preloadedData.clients.eventGuestClient,
      externalAuthClient: new ExternalAuthClient(baseUrl, eventId, appSettings.environment),
      inviteeSearchClient: new InviteeSearchClient(baseUrl, eventId, appSettings.environment),
      travelApiClient: new TravelApiClient(baseUrl, eventId, appSettings.environment, userType),
      eventPersonaClient: new EventPersonaClient(baseUrl, eventId, appSettings.environment, userType),
      flexFileClient: new FlexFileClient(baseUrl, eventId, appSettings.environment),
      DataTagsResolutionClient: new DataTagsResolutionClient(baseUrl, eventId, appSettings.environment,
        getFullLanguageFromDefaultMapping(appSettings.cultureCode), appSettings.eventContext.isPreview),
      creditCardClient: new CreditCardClient(baseUrl, eventId, appSettings.environment, userType),
      passkeyClient: new PasskeyClient(baseUrl, eventId, appSettings.environment, userType),
      websitePasswordClient: new WebsitePasswordClient(appSettings.serviceBaseUrl, eventId, appSettings.environment),
      attendeeOrderClient: new AttendeeOrderClient(baseUrl, eventId, appSettings.environment, appSettings.accessToken),
      attendeeListClient: new AttendeeListClient(baseUrl, eventId, appSettings.environment, appSettings.accessToken,
        appSettings.eventSnapshotVersion, preloadedData.assets.accountSnapshot.version),
      appointmentsClient: new AppointmentsClient(baseUrl, eventId, appSettings.environment),
      eventFeeClient: new EventFeeClient(baseUrl, eventId, appSettings.environment),
      userTextClient: new UserTextClient(baseUrl, eventId, appSettings.environment, appSettings.accessToken),
      ccpaComplianceClient: new CcpaComplianceClient(baseUrl, eventId, appSettings.environment),
      attendeeLoginClient: new AttendeeLoginClient(baseUrl, eventId, appSettings.environment, userType),
      limitsClient: new LimitsClient(baseUrl, eventId, appSettings.environment),
      translationSnapshotClient: new TranslationSnapshotClient(baseUrl, eventId, appSettings.environment, null, true),
      universalWebcastClient: new UniversalWebcastClient(baseUrl, eventId, appSettings.environment,
        appSettings.accessToken)
    },
    googleMap: {
      apiKey: appSettings.googleApiKey
    },
    appleMap: {
      token: appSettings.appleMapToken
    },
    pathInfo: {
      rootPath,
      eventId: appSettings.eventContext.eventId,
      pageId: '',
      navigationDialogConfig: { isOpen: false },
      calendarBasePath
    },
    userSession: {
      eventId: appSettings.eventContext.eventId,
      inviteeId: appSettings.eventContext.inviteeId,
      firstName: appSettings.eventContext.firstName,
      lastName: appSettings.eventContext.lastName,
      inviteeStatus: appSettings.eventContext.inviteeStatus,
      contactId: appSettings.eventContext.contactId,
      isPreview: appSettings.eventContext.isPreview,
      isPlanner: appSettings.eventContext.isPlanner,
      isTestMode: appSettings.eventContext.isTestMode,
      isAbandonedReg: appSettings.eventContext.abandonedReg,
      defaultRegTypeId: appSettings.eventContext.registrationTypeId,
      defaultRegPathId: appSettings.eventContext.registrationPathId,
      defaultRegPackId: appSettings.eventContext.registrationPackId,
      regCartId: appSettings.eventContext.regCartId,
      referenceId: appSettings.eventContext.referenceId,
      confirmationNumber: appSettings.eventContext.confirmationNumber,
      adminConfirmationNumber: appSettings.eventContext.adminConfirmationNumber,
      authenticatedContact: appSettings.eventContext.authenticatedContact,
      isSsoAdmin: appSettings.eventContext.ssoAdminFlag,
      verifiedAttendee: appSettings.eventContext.verifiedAttendee,
      adminEmailAddress: appSettings.eventContext.adminEmailAddress,
      emailAddress: appSettings.eventContext.emailAddress,
      showEventBuildWizardBanner: appSettings.eventContext.showEventBuildWizardBanner || false,
      freeTrialPurchaseCta: appSettings.eventContext.freeTrialPurchaseCta || '',
      eventBuildWizardExitUrl: queryParams.eventBuildWizardExit || '',
      licenseTypeId: appSettings.eventContext.licenseTypeId || 1,
      isFreeTrial: appSettings.eventContext.isFreeTrial || false,
      isPlannerRegMod: queryParams.plannerRegMod === 'true',
      httpReferrer: httpReferrer || '',
      isAdmin: appSettings.eventContext.isAdmin,
      abortRegistrationSecondsDelay: appSettings.eventContext.abortRegistrationSecondsDelay || 0,
      eventCode: appSettings.eventContext.eventCode
    },
    plannerRegSettings: appSettings.plannerRegSettings,
    eventLaunchWizardSettings: {
      isMerchantCPS,
      showBankAccount,
      merchantAccountId,
      openLaunchWizard,
      remittanceType
    },
    postRegistrationPaymentData: {
      webPaymentsEndpoint: appSettings.webPaymentsEndpoint,
      webPaymentsApplicationId: appSettings.webPaymentsApplicationId
    },
    partialPaymentSettings: {},
    accountSnapshotVersion: appSettings.accountSnapshotVersion,
    eventSnapshotVersion: appSettings.eventSnapshotVersion,
    travelSnapshotVersion: appSettings.travelSnapshotVersion,
    widgetFactory: new WidgetFactory(),
    attendeeList: {},
    websitePassword: {},
    multiLanguageLocale: {
      loadedLanguages: [],
      locale: appSettings.cultureCode
    },
    experiments: appSettings.experiments,
    attendeeExperience: {
      url: appSettings.attendeeExperienceUrl
    },
    appointmentsUrl: appSettings.appointmentsUrl,
    browserFeatures: await detectBrowserFeatures()
  }, {
    baseUrl: analyticsUrl,
    endpoint: analyticsUrlPath
  });

  initializeFetchWithSessionTimeout(store);

  store.dispatch(createLoadAccountSnapshotAction(preloadedData.assets.accountSnapshot));
  store.dispatch(await createLoadEventSnapshotAction(preloadedData.assets.eventSnapshot, store.getState().account));
  store.dispatch(setLocale(appSettings.cultureCode));

  if (preloadedData.assets.loginInfo) {
    await store.dispatch(loginRegistrant(preloadedData.assets.loginInfo));
  }
  // else user is logged in whenever session is alive, or should manually login through AlreadyRegisteredDialog

  const { text, account: { customFonts = [], settings: { defaultCultureCode } },
    clients: { translationSnapshotClient }, event } = store.getState();
  const { cultureCode, eventCurrencySnapshot: { isoAlphabeticCode } } = event;
  await registerTranslations(text.resolver, appSettings.cultureCode);
  initializeNetworkErrorDialog(store); // After event snapshot (for theme) and translations (for localized text)

  store.dispatch(loadTravelSnapshotDataIntoStore(preloadedData.assets.travelSnapshot, appSettings.isTravelEnabled));
  store.dispatch(loadEventTimezoneIntoStore(appSettings.eventTimezoneId, preloadedData.assets.eventTimezone.timeZones,
    preloadedData.assets.eventTimezone.translations));
  store.dispatch(setCurrencies(preloadedData.assets.currencies));
  store.dispatch(loadCapacityCounts(preloadedData.assets.capacity));
  store.dispatch(setAirports(preloadedData.assets.airports));

  const activeCustomFonts = customFonts.filter(font => font.isActive);
  if (activeCustomFonts.length > 0) {
    store.dispatch(loadCustomFonts(activeCustomFonts));
  }
  store.dispatch(setCurrencyCode(isoAlphabeticCode));
  store.dispatch(loadLanguageManagementSettings());
  store.dispatch(setSelectedTimeZone(convertEventTimezoneTranslations(text.translate,
    preloadedData.assets.eventTimezone.timeZones[appSettings.eventTimezoneId])));

  const promises = [];
  if (queryParams.locale && eventHasMultipleLanguages(event)) {
    promises.push(store.dispatch(loadLanguage(findMatchingLocaleFromAvailableLocales(queryParams.locale, event))));
  } else {
    promises.push(store.dispatch(loadLanguage(cultureCode)));
  }
  promises.push(store.dispatch(createLoadCountry(registerTranslation)));
  promises.push(store.dispatch(loadLandingPageContent(appSettings.eventContext.landingPageVariety)));
  if (eventSingleLanguageDefaultNotAccountDefault(store.getState().event, store.getState().account)) {
    promises.push(registerTranslationsFromSnapshot(text.resolver, translationSnapshotClient,
      cultureCode, defaultCultureCode, cultureCode));
  }
  await Promise.all(promises);

  /**
   * Carina RTE styles need to be added forcibly here,
   * so as to render the descriptions (in HTML) coming from the Normandy RTE,
   * to render properly here, which are missing these styles, by default.
   */
  const richTextRendererStyles = document && document.getElementById('richTextRendererStyles');
  if (richTextRendererStyles && richTextRendererStyles.innerHTML === '') {
    const rteStyles = styleString ? styleString.split('.carina-rte-styles ').join('') : '';
    document.getElementById('richTextRendererStyles').innerHTML = rteStyles;
  }

  const App = createApp(appSettings.assetRoot || '/');
  const routeHandlers = createRouteHandlers(store);

  let fixtureRoute = null;
  if (process.env.NODE_ENV !== 'production') {
    fixtureRoute = <Redirect path={`${rootPath}/`} to={`/${appSettings.eventContext.eventId}`} component={App}/>;
  }

  render(
    <Provider store={store}>
      <div>
        <Title />
        {withRoutes(App, createOnEnterHandler => (
          <Router history={history}>
            <Switch>
              <Route
                exact
                path={`${rootPath}/register`}
                component={createOnEnterHandler(routeHandlers.startRegistration)}
              />
              <Route
                exact
                path={`${rootPath}/modifyRegistration`}
                component={createOnEnterHandler(routeHandlers.modifyRegistration)}
              />
              <Route
                exact
                path={`${rootPath}/cancelRegistration`}
                component={createOnEnterHandler(routeHandlers.cancelRegistration)}
              />
              <Route
                exact
                path={`${rootPath}/decline`}
                component={createOnEnterHandler(routeHandlers.declineRegistration)}
              />
              <Route
                exact
                path={`${rootPath}/opt-out`}
                component={createOnEnterHandler(routeHandlers.optOut)}
              />
              <Route
                exact
                path={`${rootPath}/postRegistrationPayment`}
                component={createOnEnterHandler(routeHandlers.postRegistrationPayment)}
              />
              <Route
                exact
                path={`${rootPath}/unsubscribe`}
                component={createOnEnterHandler(routeHandlers.unsubscribe)}
              />
              <Route
                exact
                path={`${rootPath}/waitlist`}
                component={createOnEnterHandler(routeHandlers.waitlist)}
              />
              <Route
                exact
                path={`${rootPath}/confirmationpage`}
                component={createOnEnterHandler(routeHandlers.confirmationPage)}
              />
              <Route
                exact
                path={`${rootPath}/:pageId`}
                component={createOnEnterHandler(routeHandlers.page)}
              />
              <Route
                exact
                path={`${rootPath}/`}
                component={createOnEnterHandler(routeHandlers.index)}
              />
              {fixtureRoute}
            </Switch>
          </Router>
        ))}
        <DialogContainer
          spinnerMessage="EventGuestSide_SharedLoadingProgressWidget_SpinnerLoadingText__resx"
          message="EventGuestSide_SharedLoadingProgressWidget_MessageText__resx"
        />
        <TransparentWrapper />
      </div>
    </Provider>, document.getElementById('react-mount'));

  loadCookieConsent(store.getState());

  // remove devtools if browser plugin https://github.com/zalmoxisus/redux-devtools-extension present.
  if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test') {
    if (window.devToolsExtension) {
      LOG.info('using browser extension redux dev tools instead of normal redux dev tools');
    } else {
      const target = document.getElementById('react-mount');
      const devTarget = document.createElement('div');
      const DevTools = require('nucleus-guestside-site/src/redux/DevTools').default;
      const devToolsElement = (
        <Provider store={store}>
          <DevTools />
        </Provider>
      );
      if (target && target.parentNode) {
        target.parentNode.insertBefore(devTarget, target.nextSibling);
      } else {
        LOG.error('failed to insert devtools into DOM');
      }
      render(devToolsElement, devTarget);
    }
  }
}


export default startAppRender;
